import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { ConflictList, type ConflictListTechnician } from '@/components/scheduler/ConflictList';
import type { ScheduleConflict } from '@/lib/scheduler/scheduleConflicts';
import { useMoveConfirmation } from './MoveConfirmationFields';
import type { ScheduleNotificationChoice } from '@/hooks/jobs/useScheduleChangeNotification';
import { createUuid } from '@notifi/uuid/browser';

export interface ScheduleOverrideDialogProps {
  open: boolean;
  conflicts: ScheduleConflict[];
  /** The server's headline for the 409 (shown when there are no itemised conflicts). */
  message?: string;
  /**
   * The server refused the write whatever is acknowledged (e.g. an overlap
   * while overlapping schedules are turned off): list the conflicts, say why,
   * and offer no way to save.
   */
  blocked?: boolean;
  technicians?: ConflictListTechnician[];
  title?: string;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: (notification?: ScheduleNotificationChoice) => void;
  confirmationAppointmentId?: string;
  pending?: boolean;
}

/**
 * "Schedule anyway?" — shown when a staff write came back 409 with the
 * engine's conflicts. Confirming re-sends the write with
 * `overrideConflicts: true`; cancelling leaves the visit where it was. A
 * `blocked` refusal has only the cancel, worded as the way back to choosing
 * another time or technician.
 */
export function ScheduleOverrideDialog({
  open,
  conflicts,
  message,
  blocked = false,
  technicians,
  title,
  confirmLabel = 'Schedule anyway',
  onCancel,
  onConfirm,
  pending = false,
  confirmationAppointmentId,
}: ScheduleOverrideDialogProps) {
  const confirmation = useMoveConfirmation(
    open && !blocked ? confirmationAppointmentId : undefined,
  );
  const reason = message || 'This time conflicts with existing work.';
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // ESC / outside click = cancel (never a silent confirm).
        if (!next && !pending) onCancel();
      }}
    >
      <AlertDialogContent data-testid="schedule-override-dialog" data-blocked={blocked}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {title ?? (blocked ? "This time can't be scheduled" : 'Schedule anyway?')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-left">
              {conflicts.length > 0 ? (
                <ConflictList conflicts={conflicts} technicians={technicians} />
              ) : blocked ? null : (
                <p>{reason}</p>
              )}
              {blocked ? (
                <p>{reason} Choose another time or technician.</p>
              ) : (
                <p>Saving will record that you acknowledged these issues.</p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {confirmation.fields}
        <AlertDialogFooter>
          {/* Radix routes the Cancel click through onOpenChange(false) → onCancel. */}
          <AlertDialogCancel disabled={pending} data-testid="schedule-override-cancel">
            {blocked ? 'Choose another time' : 'Cancel'}
          </AlertDialogCancel>
          {blocked ? null : (
            <AlertDialogAction
              disabled={pending || !confirmation.canConfirm}
              onClick={(event) => {
                // Keep the dialog mounted until the retry settles; the host closes it.
                event.preventDefault();
                onConfirm(
                  confirmation.choice
                    ? { ...confirmation.choice, requestId: createUuid() }
                    : undefined,
                );
              }}
              data-testid="schedule-override-confirm"
            >
              {pending ? 'Saving…' : confirmLabel}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
